import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Space,
} from "antd";
import { Dayjs } from "dayjs";
import { toBangkokNaive } from "src/utils";
import * as React from "react";
import { CAMPAIGN_TEMPLATES } from "src/components/AffiliateCampaign/CampaignTemplates";
import { DatePicker, Select, toast } from "src/components";
import {
  AddonType,
  CAMPAIGN_TYPE_LABEL_TH,
  CampaignCreateRequest,
  CampaignStatus,
  CampaignType,
  EXTRA_CONFIG_TEMPLATES,
  getQuestTypeLabel,
  getRewardTypeLabel,
  getTargetValueLabel,
  QuestType,
  RewardType,
  useCreateAffiliateCampaign,
} from "src/hooks/affiliate";

interface ConditionFormItem {
  isUniqueUser?: boolean;
  extraConfigText?: string;
}

interface BonusFormItem {
  description?: string;
  durationInHours?: number;
  initialCoinAmount?: number;
  bonusRatio?: number;
  turnoverRatio?: number;
  maximumBonus?: number;
  maximumPerDay?: number;
  convertLimitAmount?: number;
  convertLimitRatio?: number;
}

interface QuestFormItem {
  questName: string;
  questDescription?: string;
  questType: QuestType;
  targetValue: number;
  dateRange: [Dayjs, Dayjs];
  rewardType: RewardType;
  rewardValue?: number;
  order?: number;
  isActive?: boolean;
  tier?: number;
  isAllTier?: boolean;
  conditions?: ConditionFormItem[];
  bonuses?: BonusFormItem[];
}

interface CampaignFormValues {
  campaignCode: string;
  campaignName: string;
  description?: string;
  dateRange: [Dayjs, Dayjs];
  status: CampaignStatus;
  type: CampaignType;
  isActive?: boolean;
  commissionLimit?: number;
  iconUrl?: string;
  tiers?: { tierNumber: number; commissionRate: number }[];
  addons?: {
    addonType: AddonType;
    minValue?: number;
    maxValue?: number;
    limitPerUser?: number;
    commissionRate: number;
  }[];
  quests?: QuestFormItem[];
}

type CreateMode = "template" | "manual";

const campaignTypeOptions = Object.values(CampaignType).map((v) => ({
  value: v,
  label: CAMPAIGN_TYPE_LABEL_TH[v],
}));

const statusOptions = [
  { value: CampaignStatus.DRAFT, label: "DRAFT" },
  { value: CampaignStatus.ACTIVE, label: "ACTIVE" },
  { value: CampaignStatus.INACTIVE, label: "INACTIVE" },
  { value: CampaignStatus.COMPLETED, label: "COMPLETED" },
];

const addonTypeOptions = [
  { value: AddonType.REFERRAL_COUNT, label: "REFERRAL_COUNT" },
  { value: AddonType.SALE_VOLUME, label: "SALE_VOLUME" },
  { value: AddonType.SEGMENT_BASED, label: "SEGMENT_BASED" },
];

const questTypeOptions = Object.values(QuestType).map((v) => ({
  value: v,
  label: getQuestTypeLabel(v),
}));
const rewardTypeOptions = Object.values(RewardType).map((v) => ({ value: v, label: getRewardTypeLabel(v) }));

export type AffiliateCampaignCreateModalProps = {
  onSuccess?: () => void;
};

export const AffiliateCampaignCreateModal = NiceModal.create(
  ({ ...props }: AffiliateCampaignCreateModalProps) => {
    const { visible, hide, remove } = useModal();
    const [form] = Form.useForm<CampaignFormValues>();
    const [mode, setMode] = React.useState<CreateMode>("template");
    const [templateId, setTemplateId] = React.useState<string | undefined>(undefined);
    const { mutate: createCampaign, isLoading } = useCreateAffiliateCampaign({
      onSuccess: () => {
        form.resetFields();
        setMode("template");
        setTemplateId(undefined);
        props.onSuccess?.();
        hide();
      },
    });

    const handleClose = () => {
      hide();
    };

    const applyTemplate = (id: string) => {
      const template = CAMPAIGN_TEMPLATES.find((t) => t.id === id);
      if (!template) return;
      setTemplateId(id);
      // Reset first so Form.List rows (tiers/addons) don't merge with stale entries.
      form.resetFields();
      form.setFieldsValue(template.build());
    };

    const handleModeChange = (next: CreateMode) => {
      setMode(next);
      if (next === "manual") {
        setTemplateId(undefined);
      }
    };

    const handleInsertTemplate = (questFieldName: number, condFieldName: number) => {
      const questType: QuestType | undefined = form.getFieldValue([
        "quests",
        questFieldName,
        "questType",
      ]);
      if (!questType) {
        toast.error("เลือก Quest Type ก่อน");
        return;
      }
      const template = EXTRA_CONFIG_TEMPLATES[questType];
      if (!template) {
        toast.error(`Quest type ${questType} ไม่มี template extra_config`);
        return;
      }
      form.setFields([
        {
          name: ["quests", questFieldName, "conditions", condFieldName, "extraConfigText"],
          value: JSON.stringify(template, null, 2),
        },
      ]);
    };

    const handleFinish = (values: CampaignFormValues) => {
      const [start, end] = values.dateRange || [];
      if (!start || !end) {
        toast.error("กรุณาเลือกวันที่เริ่ม-สิ้นสุดของ campaign");
        return;
      }

      let invalidQuestDate = false;
      let invalidExtraConfig = false;
      let missingBonus = false;
      const payload: CampaignCreateRequest = {
        campaignCode: values.campaignCode,
        campaignName: values.campaignName,
        description: values.description,
        startDate: toBangkokNaive(start),
        endDate: toBangkokNaive(end),
        status: values.status,
        type: values.type,
        isActive: values.isActive,
        commissionLimit: values.commissionLimit?.toString(),
        iconUrl: values.iconUrl?.trim() || undefined,
        tiers: (values.tiers || []).map((t) => ({
          tierNumber: Number(t.tierNumber),
          commissionRate: Number(t.commissionRate).toString(),
        })),
        addons: (values.addons || []).map((a) => ({
          addonType: a.addonType,
          minValue: a.minValue ?? null,
          maxValue: a.maxValue ?? null,
          limitPerUser: a.limitPerUser ?? null,
          commissionRate: Number(a.commissionRate).toString(),
        })),
        quests: (values.quests || []).map((q) => {
          const [qStart, qEnd] = q.dateRange || [];
          if (!qStart || !qEnd) invalidQuestDate = true;
          // reward_type = BONUS → ต้องมี affiliate_campaign_bonus อย่างน้อย 1 รายการ (มี Initial Coin)
          if (
            q.rewardType === RewardType.BONUS &&
            !(q.bonuses || []).some((b) => Number(b.initialCoinAmount ?? 0) > 0)
          ) {
            missingBonus = true;
          }
          return {
            questName: q.questName,
            questDescription: q.questDescription,
            questType: q.questType,
            targetValue: Number(q.targetValue),
            startDate: qStart ? toBangkokNaive(qStart) : "",
            endDate: qEnd ? toBangkokNaive(qEnd) : "",
            rewardType: q.rewardType,
            rewardValue: q.rewardValue != null ? Number(q.rewardValue).toString() : undefined,
            order: q.order,
            isActive: q.isActive,
            tier: q.isAllTier ? null : q.tier ?? null,
            isAllTier: q.isAllTier,
            conditions: (q.conditions || []).map((c) => {
              let extraConfig: Record<string, unknown> | null = null;
              const text = (c.extraConfigText || "").trim();
              if (text) {
                try {
                  extraConfig = JSON.parse(text);
                } catch {
                  invalidExtraConfig = true;
                }
              }
              return {
                // metricType/operator/targetValue dropped — backend keys off quest.questType
                timeWindowDay: null,
                isUniqueUser: c.isUniqueUser ?? false,
                extraConfig,
              };
            }),
            bonuses: (q.bonuses || []).map((b) => ({
              description: b.description,
              durationInHours: b.durationInHours ?? 0,
              initialCoinAmount: b.initialCoinAmount ?? 0,
              bonusRatio: b.bonusRatio ?? 0,
              turnoverRatio: b.turnoverRatio ?? 0,
              maximumBonus: b.maximumBonus ?? 0,
              maximumPerDay: b.maximumPerDay ?? 0,
              convertLimitAmount: b.convertLimitAmount ?? 0,
              convertLimitRatio: b.convertLimitRatio ?? 1,
            })),
          };
        }),
      };

      if (invalidQuestDate) {
        toast.error("กรุณาเลือกวันที่ของ quest ให้ครบ");
        return;
      }
      if (invalidExtraConfig) {
        toast.error("extra_config ต้องเป็น JSON ที่ถูกต้อง");
        return;
      }
      if (missingBonus) {
        toast.error("reward type โบนัส ต้องเพิ่ม Bonus อย่างน้อย 1 รายการ (ระบุ Initial Coin)");
        return;
      }
      createCampaign(payload);
    };

    return (
      <Modal
        centered
        width={1000}
        visible={visible}
        afterClose={remove}
        closable={false}
        title="สร้าง Affiliate Campaign"
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
            <Button key="cancel" size="large" onClick={handleClose} style={{ width: 100 }}>
              ยกเลิก
            </Button>
            <Button
              key="submit"
              type="primary"
              size="large"
              loading={isLoading}
              onClick={() => form.submit()}
              style={{ width: 200 }}
            >
              สร้าง Campaign
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onFinish={handleFinish}
          preserve={false}
          initialValues={{
            status: CampaignStatus.DRAFT,
            type: CampaignType.COMMISSION,
            isActive: true,
            tiers: [],
            addons: [],
            quests: [],
          }}
          style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8 }}
        >
          <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Radio.Group
                value={mode}
                onChange={(e) => handleModeChange(e.target.value as CreateMode)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="template">เทมเพลต</Radio.Button>
                <Radio.Button value="manual">แมนนวล</Radio.Button>
              </Radio.Group>
              {mode === "template" && (
                <Row gutter={[8, 0]} align="middle">
                  <Col flex="auto">
                    <Select
                      value={templateId}
                      onChange={(v) => applyTemplate(v as string)}
                      placeholder="เลือกเทมเพลต"
                      style={{ width: "100%" }}
                      options={CAMPAIGN_TEMPLATES.map((t) => ({ value: t.id, label: t.name }))}
                    />
                  </Col>
                </Row>
              )}
              {mode === "template" && templateId && (
                <span style={{ color: "#888", fontSize: 12 }}>
                  {CAMPAIGN_TEMPLATES.find((t) => t.id === templateId)?.description}
                </span>
              )}
            </Space>
          </Card>

          <Card title="ข้อมูลพื้นฐาน" size="small">
            <Row gutter={[16, 0]}>
              <Col span={8}>
                <Form.Item
                  label="Campaign Code"
                  name="campaignCode"
                  rules={[
                    { required: true, message: "กรุณาใส่ campaign code" },
                    { max: 20, message: "ไม่เกิน 20 ตัวอักษร" },
                  ]}
                >
                  <Input placeholder="SUMMER25" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Campaign Name"
                  name="campaignName"
                  rules={[
                    { required: true, message: "กรุณาใส่ชื่อ campaign" },
                    { max: 255, message: "ไม่เกิน 255 ตัวอักษร" },
                  ]}
                >
                  <Input placeholder="Summer 2025" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="สถานะ"
                  name="status"
                  rules={[{ required: true, message: "กรุณาเลือกสถานะ" }]}
                >
                  <Select options={statusOptions} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="ช่วงเวลา campaign"
                  name="dateRange"
                  rules={[{ required: true, message: "กรุณาเลือกช่วงเวลา" }]}
                >
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Commission Limit" name="commissionLimit">
                  <InputNumber min={0} style={{ width: "100%" }} placeholder="10000.00" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="isActive" valuePropName="checked" label=" ">
                  <Checkbox>Active</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="ประเภท Campaign"
                  name="type"
                  rules={[{ required: true, message: "กรุณาเลือกประเภท" }]}
                >
                  <Select options={campaignTypeOptions} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label="Icon URL" name="iconUrl">
                  <Input placeholder="https://... (ไม่บังคับ)" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="คำอธิบาย" name="description">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Divider />

          <Card title="Tiers" size="small">
            <Form.List name="tiers">
              {(fields, { add, remove: removeTier }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Row gutter={[16, 0]} key={key} align="middle">
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          label="Tier Number"
                          name={[name, "tierNumber"]}
                          rules={[{ required: true, message: "ระบุ tier number" }]}
                        >
                          <InputNumber min={1} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...rest}
                          label="Commission Rate (%)"
                          name={[name, "commissionRate"]}
                          rules={[{ required: true, message: "ระบุ commission rate" }]}
                        >
                          <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col>
                        <MinusCircleOutlined onClick={() => removeTier(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                    เพิ่ม Tier
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          <Divider />

          <Card title="Addons" size="small">
            <Form.List name="addons">
              {(fields, { add, remove: removeAddon }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Row gutter={[16, 0]} key={key} align="middle">
                      <Col span={5}>
                        <Form.Item
                          {...rest}
                          label="Addon Type"
                          name={[name, "addonType"]}
                          rules={[{ required: true, message: "เลือก addon type" }]}
                        >
                          <Select options={addonTypeOptions} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} label="Min Value" name={[name, "minValue"]}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} label="Max Value" name={[name, "maxValue"]}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...rest}
                          label="Limit/User (บาท/วัน)"
                          name={[name, "limitPerUser"]}
                        >
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...rest}
                          label="Commission Rate (%)"
                          name={[name, "commissionRate"]}
                          rules={[{ required: true, message: "ระบุ commission rate" }]}
                        >
                          <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <MinusCircleOutlined onClick={() => removeAddon(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                    เพิ่ม Addon
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          <Divider />

          <Card title="Quests" size="small">
            <Form.List name="quests">
              {(questFields, { add: addQuest, remove: removeQuest }) => (
                <>
                  {questFields.map((questField) => (
                    <Card
                      key={questField.key}
                      type="inner"
                      size="small"
                      style={{ marginBottom: 16 }}
                      title={`Quest #${questField.name + 1}`}
                      extra={
                        <MinusCircleOutlined onClick={() => removeQuest(questField.name)} />
                      }
                    >
                      <Row gutter={[16, 0]}>
                        <Col span={8}>
                          <Form.Item
                            label="ชื่อ Quest"
                            name={[questField.name, "questName"]}
                            rules={[{ required: true, message: "ระบุชื่อ quest" }]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            label="Quest Type"
                            name={[questField.name, "questType"]}
                            rules={[{ required: true, message: "เลือก quest type" }]}
                          >
                            <Select options={questTypeOptions} width="100%" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, cur) =>
                              prev.quests?.[questField.name]?.questType !==
                              cur.quests?.[questField.name]?.questType
                            }
                          >
                            {({ getFieldValue }) => (
                              <Form.Item
                                label={getTargetValueLabel(
                                  getFieldValue(["quests", questField.name, "questType"]),
                                )}
                                name={[questField.name, "targetValue"]}
                                rules={[{ required: true, message: "ระบุ target value" }]}
                              >
                                <InputNumber min={0} style={{ width: "100%" }} />
                              </Form.Item>
                            )}
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="ช่วงเวลา Quest"
                            name={[questField.name, "dateRange"]}
                            rules={[{ required: true, message: "เลือกช่วงเวลา" }]}
                          >
                            <DatePicker.RangePicker
                              style={{ width: "100%" }}
                              format="DD/MM/YYYY HH:mm"
                              showTime={{ format: "HH:mm" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            label="Reward Type"
                            name={[questField.name, "rewardType"]}
                            rules={[{ required: true, message: "เลือก reward type" }]}
                          >
                            <Select options={rewardTypeOptions} width="100%" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="Reward Value" name={[questField.name, "rewardValue"]}>
                            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label="คำอธิบาย" name={[questField.name, "questDescription"]}>
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item label="Order" name={[questField.name, "order"]}>
                            <InputNumber min={0} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, cur) =>
                              prev.quests?.[questField.name]?.isAllTier !==
                              cur.quests?.[questField.name]?.isAllTier
                            }
                          >
                            {({ getFieldValue }) => {
                              const allTier = getFieldValue([
                                "quests",
                                questField.name,
                                "isAllTier",
                              ]);
                              return (
                                <Form.Item
                                  label="Tier"
                                  name={[questField.name, "tier"]}
                                  rules={[
                                    {
                                      required: !allTier,
                                      message: "ระบุ tier หรือเลือก All Tier",
                                    },
                                  ]}
                                >
                                  <InputNumber
                                    min={1}
                                    style={{ width: "100%" }}
                                    disabled={allTier}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            name={[questField.name, "isAllTier"]}
                            valuePropName="checked"
                            label=" "
                          >
                            <Checkbox
                              onChange={(e) => {
                                if (e.target.checked) {
                                  form.setFields([
                                    {
                                      name: ["quests", questField.name, "tier"],
                                      value: undefined,
                                      errors: [],
                                    },
                                  ]);
                                }
                              }}
                            >
                              All Tier
                            </Checkbox>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            name={[questField.name, "isActive"]}
                            valuePropName="checked"
                            label=" "
                            initialValue
                          >
                            <Checkbox>Active</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Divider orientation="left" plain>
                        Conditions
                      </Divider>
                      <Form.List name={[questField.name, "conditions"]}>
                        {(condFields, { add: addCond, remove: removeCond }) => (
                          <>
                            {condFields.map((condField) => (
                              <Row gutter={[16, 0]} key={condField.key}>
                                <Col span={5}>
                                  <Form.Item
                                    name={[condField.name, "isUniqueUser"]}
                                    valuePropName="checked"
                                    label=" "
                                  >
                                    <Checkbox>Unique</Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col span={18}>
                                  <Form.Item
                                    label={
                                      <Space size="small">
                                        <span>Extra Config (JSON)</span>
                                        <Button
                                          size="small"
                                          type="link"
                                          onClick={() =>
                                            handleInsertTemplate(questField.name, condField.name)
                                          }
                                          style={{ padding: 0 }}
                                        >
                                          ใส่ template
                                        </Button>
                                      </Space>
                                    }
                                    name={[condField.name, "extraConfigText"]}
                                  >
                                    <Input.TextArea
                                      rows={2}
                                      placeholder='{"min_per_user": 2}'
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ display: "flex", alignItems: "center" }}>
                                  <MinusCircleOutlined onClick={() => removeCond(condField.name)} />
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() => addCond()}
                              icon={<PlusOutlined />}
                              block
                            >
                              เพิ่ม Condition
                            </Button>
                          </>
                        )}
                      </Form.List>

                      <Divider orientation="left" plain>
                        Bonuses
                      </Divider>
                      <Form.List name={[questField.name, "bonuses"]}>
                        {(bonusFields, { add: addBonus, remove: removeBonus }) => (
                          <>
                            {bonusFields.map((bonusField) => (
                              <Card
                                key={bonusField.key}
                                size="small"
                                style={{ marginBottom: 12, background: "#fafafa" }}
                                type="inner"
                                title={`Bonus #${bonusField.name + 1}`}
                                extra={
                                  <MinusCircleOutlined
                                    onClick={() => removeBonus(bonusField.name)}
                                  />
                                }
                              >
                                <Row gutter={[16, 0]}>
                                  <Col span={12}>
                                    <Form.Item label="คำอธิบาย" name={[bonusField.name, "description"]}>
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Duration (hours)" name={[bonusField.name, "durationInHours"]}>
                                      <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Initial Coin" name={[bonusField.name, "initialCoinAmount"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Bonus Ratio" name={[bonusField.name, "bonusRatio"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Turnover Ratio" name={[bonusField.name, "turnoverRatio"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Maximum Bonus" name={[bonusField.name, "maximumBonus"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Max per Day" name={[bonusField.name, "maximumPerDay"]}>
                                      <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Convert Limit Amount" name={[bonusField.name, "convertLimitAmount"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item label="Convert Limit Ratio" name={[bonusField.name, "convertLimitRatio"]}>
                                      <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() => addBonus()}
                              icon={<PlusOutlined />}
                              block
                            >
                              เพิ่ม Bonus
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      addQuest({
                        isActive: true,
                        conditions: [],
                        bonuses: [],
                      })
                    }
                    icon={<PlusOutlined />}
                    block
                  >
                    เพิ่ม Quest
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </Modal>
    );
  },
);

export default AffiliateCampaignCreateModal;
